import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type {
  ProductReadMarkerMap,
  ProductReadScopeKey,
} from "@/types/frontend-types";
import {
  selectMergedProductReadItems,
  selectProductReadsFromItems,
  selectUpdatedProductReadMarker,
  toProductReadScopeKey,
} from "./read-tracking-selectors";
import type {
  MergeProductReadItemsInput,
  MergeProductReadItems,
  SessionProductReadMarkers,
  UpdateLocalProductReadMarkerInput,
  UpdateLocalProductReadMarker,
} from "./use-session-read-tracking.types";

type ProductReadSetter = Dispatch<SetStateAction<ProductReadMarkerMap>>;

interface ProductReadActionInput {
  setProductReads: ProductReadSetter;
  productReadUpdatedAtByScopeRef: MutableRefObject<
    Record<ProductReadScopeKey, number>
  >;
}

export function useSessionProductReadMarkers(): SessionProductReadMarkers {
  const [productReads, setProductReads] = useState<ProductReadMarkerMap>({});
  const productReadUpdatedAtByScopeRef = useRef<
    Record<ProductReadScopeKey, number>
  >({});
  const resetProductReadMarkers = useProductReadResetAction({
    setProductReads,
    productReadUpdatedAtByScopeRef,
  });
  const mergeProductReadItems = useProductReadMergeAction({
    setProductReads,
    productReadUpdatedAtByScopeRef,
  });
  const updateLocalProductReadMarker = useLocalProductReadMarkerAction({
    setProductReads,
    productReadUpdatedAtByScopeRef,
  });

  return {
    productReads,
    mergeProductReadItems,
    resetProductReadMarkers,
    updateLocalProductReadMarker,
  };
}

function useProductReadResetAction({
  setProductReads,
  productReadUpdatedAtByScopeRef,
}: ProductReadActionInput): () => void {
  return useCallback((): void => {
    setProductReads({});
    productReadUpdatedAtByScopeRef.current = {};
  }, [productReadUpdatedAtByScopeRef, setProductReads]);
}

function useProductReadMergeAction({
  setProductReads,
  productReadUpdatedAtByScopeRef,
}: ProductReadActionInput): MergeProductReadItems {
  return useCallback(
    (input: MergeProductReadItemsInput): void => {
      const { items, replace } = input;
      const nowTimestamp = Date.now();
      if (replace) {
        const selection = selectProductReadsFromItems({
          items,
          nowTimestamp,
        });
        productReadUpdatedAtByScopeRef.current = selection.updatedAtByScope;
        setProductReads(selection.productReads);
        return;
      }

      setProductReads((previous) => {
        const selection = selectMergedProductReadItems({
          previous,
          previousUpdatedAtByScope: productReadUpdatedAtByScopeRef.current,
          items,
          nowTimestamp,
        });

        if (!selection.didChange) return previous;

        productReadUpdatedAtByScopeRef.current = selection.updatedAtByScope;
        return selection.productReads;
      });
    },
    [productReadUpdatedAtByScopeRef, setProductReads],
  );
}

function useLocalProductReadMarkerAction({
  setProductReads,
  productReadUpdatedAtByScopeRef,
}: ProductReadActionInput): UpdateLocalProductReadMarker {
  return useCallback(
    (input: UpdateLocalProductReadMarkerInput): void => {
      const { productId, collabsReadAt, productSeenAt } = input;
      const scopeKey = toProductReadScopeKey({ productId });
      const nowTimestamp = Date.now();

      setProductReads((previous) => {
        const selection = selectUpdatedProductReadMarker({
          previous,
          productId,
          collabsReadAt,
          productSeenAt,
        });

        if (!selection.didChange) return previous;

        productReadUpdatedAtByScopeRef.current = {
          ...productReadUpdatedAtByScopeRef.current,
          [scopeKey]: nowTimestamp,
        };

        return selection.productReads;
      });
    },
    [productReadUpdatedAtByScopeRef, setProductReads],
  );
}
